import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Invoice } from '@app/core/business/invoice/invoice';
import { InvoiceType } from '@app/core/business/invoice/invoice-type';
import { delay, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Bill } from './bill';
import { OnlinePaymentTransaction } from './online-payment-transaction';
import { PayableInvoice } from './payable-invoice';
import { OnlinePayementInfo } from './online-payement-info';
import { PrintService } from '@app/core/service/print.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ETransactionQuestion } from './etransaction-question';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  private baseUrl: string = environment.baseApiUrl;

  /** Les informations du service de paiement en ligne */
  public onlinePaymentInfosHtmlRaw: SafeHtml = "Test";

  /** Permet d'afficher/masquer les informations du service de paiement en ligne téléchargées */
  public showPayInformation: boolean = false;

  constructor(
    private http: HttpClient,
    private printService: PrintService,
    private sanitizer: DomSanitizer) {}

  /**
   * Get all invoices /companies/COMPANY_CODE/customers/CUSTOMER_CODE/invoices
   * @param companyCode Code of the company
   * @param customerCode Code of the customer
   */
  public getInvoices(
    companyCode: string,
    customerCode: string
  ): Observable<Invoice[]> {
    if (environment.useMock) {
      const rows: Invoice[] = new Array<Invoice>();

      const names = [
        'Facture 22/04',
        'Facture 6/06',
        'Facture 2/07',
        'Facture 24/08',
        'Facture 30/09',
      ];

      for (let i = 0; i <= 40; i++) {
        rows.push({
          code: names[i % 5],
          customerCode: customerCode,
          date: new Date(),
          type: InvoiceType.CREDIT_NOTE,
          deliveryNoteCodes: [
            'bl-' + Math.floor(Math.random() * 1000),
            'bl-' + Math.floor(Math.random() * 1000),
          ],
          orderCodes: [
            'order-' + Math.floor(Math.random() * 1000),
            'order-' + Math.floor(Math.random() * 1000),
          ],
          claimCodes: [
            'claim-' + Math.floor(Math.random() * 1000),
            'claim-' + Math.floor(Math.random() * 1000),
          ],
          paymentStatus: 'payé',
          paymentDueDate: new Date('2018-9-10'),
          price: {
            total: Math.floor(Math.random() * 1000).toString(),
            totalAfterTaxes: Math.floor(Math.random() * 1000).toString(),
          },
          lines: [],
        });
      }

      return of(rows).pipe(delay(1000));
    }

    return this.http.get<Invoice[]>(
      `${
        this.baseUrl
      }/companies/${companyCode}/customers/${customerCode}/invoices`
    );
  }

  /**
   * Permet de récupérer la liste des factures/avoirs à utiliser dans une transaction de paiement
   * @param companyCode Le code de la société
   * @param customerCode Le code du client
   */
  public getPayableInvoices(
    companyCode: string,
    customerCode: string
  ): Observable<PayableInvoice[]> {
    return this.http.get<PayableInvoice[]>(
      `${
        this.baseUrl
      }/companies/${companyCode}/customers/${customerCode}/PayableInvoices`
    );
  }

  public validateTransaction(
    companyCode: string,
    customerCode: string,
    value: number,
    relatedDocument: string[],
  ): any {
    return this.http.post<any>(
      `${
        this.baseUrl
      }/companies/${companyCode}/customers/${customerCode}/OnlinePaymentTransaction`,
      {
        "value": value,
        "RelatedDocuments": relatedDocument,
      }
    );
  }

  /**
   * Récupération des informations nécessaires pour le paiement en ligne CA
   * @param companyCode Code de la société
   * @param customerCode Code client
   * @param value Montant total à payer
   * @param relatedDocument Liste des documents concernés par le paiement
   * @returns
   */
  public validateTransactionCA(companyCode: string, customerCode: string, value: number, relatedDocument: string[], originalDocument: string[]): Observable<ETransactionQuestion> {

    return this.http.post<ETransactionQuestion>(
      `${this.baseUrl}/companies/${companyCode}/customers/${customerCode}/ETransactionCA/DirectPayment`,
      {
        "value": value,
        "RelatedDocuments": relatedDocument,
        "OriginalDocuments": originalDocument
      }
    );
  }

  public getInvoice(
    companyCode: string,
    customerCode: string,
    invoiceCode: string
  ): Observable<Invoice> {
    return this.http.get<Invoice>(
      `${
        this.baseUrl
      }/companies/${companyCode}/customers/${customerCode}/invoices/${invoiceCode}`
    );
  }

  public downloadPdf(
    companyCode: string,
    customerCode: string,
    invoiceCode: string
  ): Observable<any> {
    return this.http.get(
      `${
        this.baseUrl
      }/companies/${companyCode}/customers/${customerCode}/invoices/${invoiceCode}/pdf`,
      { responseType: 'arraybuffer' }
    );
  }

  /**
   * Permet au client de récupérer un extrait de compte au sein de la société
   * @param companyCode Le code de la société
   * @param customerCode Le code du client
   * @param startDate La date de début de l'extrait de compte
   * @param endDate La date de fin de l'extrait de compte
   */
  public downloadAccountBookPdf(
    companyCode: string,
    customerCode: string,
    startDate: string,
    endDate: string
  ): Observable<any> {
    return this.http.get(
      `${
      this.baseUrl
      }/companies/${companyCode}/customers/${customerCode}/AccountBook/Pdf?startDate=${startDate}&endDate=${endDate}`,
      { responseType: 'arraybuffer' }
    );
  }

  /**
  * Permet au client de récupérer un relevé à la décade
  * @param companyCode Le code de la société
  * @param customerCode Le code du client
  * @param startDate La date de début de l'extrait de compte
  * @param endDate La date de fin de l'extrait de compte
  */
  public downloadCustomerRelevePdf(
    companyCode: string,
    customerCode: string,
    startDate: string,
    endDate: string
  ): Observable<any> {
    return this.http.get(
      `${
      this.baseUrl
      }/companies/${companyCode}/customers/${customerCode}/CustomerReleve/Pdf?startDate=${startDate}&endDate=${endDate}`,
      { responseType: 'arraybuffer' }
    );
  }

  /**
   * Permet de récupérer le détail des traites
   * @param companyCode Le code de la société
   * @param customerCode Le code du client
   */
  public getBills(
    companyCode: string,
    customerCode: string
  ): Observable<Bill[]> {
    return this.http.get<Bill[]>(
      `${
        this.baseUrl
      }/companies/${companyCode}/customers/${customerCode}/Bills`
    );
  }

  /**
   * Permet de récupérer les informations de paiement en ligne
   */
  public getOnlinePaymentInfosHtmlRaw(companyCode: string): void {
    this.http.get(`${this.baseUrl}/companies/${companyCode}/OnlinePaymentInfos`, { responseType: 'text' }).subscribe(
      onlinePaymentInfosHtmlRaw => this.onlinePaymentInfosHtmlRaw = this.sanitizer.bypassSecurityTrustHtml(onlinePaymentInfosHtmlRaw)
    );
  }

  /**
   * Permet d'afficher/masquer les informations sur le paiement en ligne
   */
  public togglePayInformation() {
    this.showPayInformation=!this.showPayInformation;
  }
}
